import './style.scss';

import { deleteDoc, doc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/init";
import { getRequestShop } from "../../redux/slices/env";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setArticle } from '../../redux/slices/articles';
import { getNavigation, setArticleIndex } from '../../redux/slices/navigation';

import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  ButtonGroup,
  InlineGrid,
  InlineStack,
  Label,
  Page,
  Select,
  Spinner,
  Text
} from '@shopify/polaris';

import {
  ChevronUpIcon,
  ChevronDownIcon,
  DeleteIcon,
  EditIcon,
  MenuIcon,
  PinFilledIcon,
  PinIcon,
} from '@shopify/polaris-icons';

import { DateTime } from 'luxon';

import useWindowSize from 'hooks/useWindowSize';

import Skeleton from '@/app/layout/Skeleton';
import DeleteButton from 'components/DeleteButton/DeleteButton';
import ContentEditor from './ContentEditor';
import TitleEditor from './TitleEditor';

import type { Article as ArticleType } from '@/shared/typings/types/guide';
import { compareHtmlAndRemoveImages, removeImagesNotInHtml } from 'utils/actions';
import { getQueryParam } from 'utils/helpers';

interface ArticleProps {
  article: ArticleType;
  articleId: string;
  articleRef: string;
  loading?: boolean;
  isNew?: boolean;
  firstArticlePath?: string;
  onActivateMobileNavigation?: () => void;
  historyReplace: (path: string) => void;
}

function Article(props: ArticleProps) {
  const {
    article,
    articleId,
    articleRef,
    loading,
    isNew,
    onActivateMobileNavigation,
    historyReplace,
  } = props

  const shop = useAppSelector(getRequestShop);

  const {
    sections = {},
    sections_list = [],
    navigation_items = [],
    articles_index = {},
  } = useAppSelector(getNavigation);

  const [lazyArticle, setLazyArticle] = useState<any>(article)
  const [edit, setEdit] = useState(isNew)
  const [uploadedImages, setUploadedImages] = useState<string[]>([])
  const [pageTop, setPageTop] = useState(0)
  const [editorProcessing, setEditorProcessing] = useState(false)

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const articleElRef = useRef<HTMLElement>(null);


  const { window: { width } } = useWindowSize();

  const section = getQueryParam('section')

  const collectionRef = `shops/${shop}/articles`

  const navigationRef = `shops/${shop}/settings/navigation`

  useEffect(() => {
    function watchClick(e: any) {

      if (edit) return

      if (e.target.nodeName === 'A') {
        let el = e.target
        let href = el.href

        e.preventDefault()

        if (href.startsWith(window.location.origin)) {
          navigate(href.replace(window.location.origin, ''))
        } else {
          window.open(href)
        }

      } else if (e.target.nodeName === 'SPAN') {
        let dataset

        if (e.target.dataset.denotationChar) {
          dataset = e.target.dataset
        } else if (e.target.parentNode.dataset.denotationChar) {
          dataset = e.target.parentNode.dataset
        } else if (e.target.parentNode.parentNode.dataset.denotationChar) {
          dataset = e.target.parentNode.parentNode.dataset
        }

        if (!dataset) return

        let { path, source, id } = dataset

        navigate(`${path}${id}`)
      }
    }

    document.body.addEventListener("click", watchClick, false)

    return () => document.body.removeEventListener("click", watchClick)
  }, [navigate, edit, shop])

  useLayoutEffect(() => {
    let el = document.getElementById('AC_Actions_Bar')
    if (el) setPageTop(el.getBoundingClientRect().height)
  }, [edit])

  useEffect(() => {
    if (isNew) setEdit(true)
  }, [isNew]);

  const handleImageUploaded = useCallback((image: string) => setUploadedImages(x => [...x, image]), [])

  const toggleEdit = useCallback(() => setEdit(x => !x), [])

  const handleDelete = useCallback(() => {
    setEdit(false)
    let articlesIndex = { ...articles_index }
    delete articlesIndex[articleId]
    deleteDoc(doc(db, articleRef))
    updateDoc(doc(db, navigationRef), { articles_index: articlesIndex })
  }, [articleRef, navigationRef, articleId, articles_index])


  const handleCancel = useCallback(() => {
    setEdit(false)
    setLazyArticle(article)
    setTimeout(() => {
      if (isNew) historyReplace('/articles')
    })
  }, [article, historyReplace, isNew])


  const saveArticle = useCallback((passedArticle?: any) => {
    const generateId = () => `${Number(new Date())}${Math.floor(Math.random() * 10)}`
    const updated = DateTime.local().toISO()
    const id = !isNew ? articleId : generateId()
    let article: any = passedArticle || lazyArticle
    article = { ...article, id, updated, title: article.title || '' }
    const { title, section, pinned = false } = article
    const oldSection = articles_index[id]?.section;
    const index = (section === oldSection) ? articles_index[id]?.index : undefined;
    const articleIndex = { id, title, section, pinned, ...(index !== undefined && { index }) }

    dispatch(setArticle({ ...article, ...(index !== undefined && { index }) }));
    dispatch(setArticleIndex(articleIndex));
    console.log(article);

    if (isNew) {
      setDoc(doc(db, collectionRef, id), { ...article, ...(index !== undefined && { index }) })
    } else {
      setDoc(doc(db, articleRef), { ...article, ...(index !== undefined && { index }) })
    }

    setDoc(doc(db, navigationRef), { articles_index: { ...articles_index, [id]: articleIndex } }, { merge: true })

    return id
  }, [lazyArticle, articles_index, articleId, isNew, collectionRef, articleRef, navigationRef, dispatch])

  const handleChange = useCallback((val: any, key: string, save?: boolean) => {
    if (!edit && !save) return
    let article = { ...lazyArticle, [key]: val }
    setLazyArticle(article)
    if (save) saveArticle(article)
  }, [lazyArticle, saveArticle, edit])

  const saveAndFinishEdit = useCallback(() => {
    setEdit(false)
    let oldHtml = article.html
    let newHtml = lazyArticle.html
    let id = saveArticle()
    compareHtmlAndRemoveImages(oldHtml || '', newHtml || '')
    setTimeout(() => {
      if (isNew) historyReplace(`/articles/${id}`) //  inside timeout so that the edit has been set and does not fire the nav guard
    });
  }, [saveArticle, historyReplace, isNew, article.html, lazyArticle.html])

  const handlePin = useCallback(() => {
    handleChange(!article.pinned, 'pinned', true)
  }, [article.pinned, handleChange])

  // Get all articles in the current section
  const sectionArticles = useMemo(() => {
    if (!lazyArticle.section) return [];
    // Get all articles in this section, sorted by index/title
    const siblingItems = Object.values(articles_index)
      .filter((a: any) => a.section === lazyArticle.section);

    // Sort them exactly the same way buildNavigation does!
    const sortArticles = (a: any, b: any) => {
      const aIndex = a.index !== undefined ? a.index : Number.MAX_SAFE_INTEGER;
      const bIndex = b.index !== undefined ? b.index : Number.MAX_SAFE_INTEGER;
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      return a.title.localeCompare(b.title);
    };

    return siblingItems.sort(sortArticles);
  }, [articles_index, lazyArticle.section]);

  const currentArticleIndex = useMemo(() => {
    return sectionArticles.findIndex(a => a.id === articleId);
  }, [sectionArticles, articleId]);

  const isFirstArticle = currentArticleIndex === 0;
  const isLastArticle = currentArticleIndex === sectionArticles.length - 1;
  const isSectionChanged = lazyArticle.section !== article.section;

  const moveArticle = useCallback((direction: 'up' | 'down') => {
    if (isNew) return;
    const targetIdx = direction === 'up' ? currentArticleIndex - 1 : currentArticleIndex + 1;
    if (targetIdx < 0 || targetIdx >= sectionArticles.length) return;

    // Swap our article with the sibling
    const sibling = sectionArticles[targetIdx];

    // Re-assign index values for all articles in the section to make sure they are sequential
    const updatedArticlesIndex = { ...articles_index };

    // Construct the new list with swapped positions
    const reordered = [...sectionArticles];
    const temp = reordered[currentArticleIndex];
    reordered[currentArticleIndex] = sibling;
    reordered[targetIdx] = temp;

    reordered.forEach((art, idx) => {
      updatedArticlesIndex[art.id] = {
        ...updatedArticlesIndex[art.id],
        index: idx
      };
    });

    // Save index map to Firestore
    setDoc(doc(db, navigationRef), { articles_index: updatedArticlesIndex }, { merge: true });
  }, [currentArticleIndex, sectionArticles, articles_index, db, navigationRef, isNew]);

  useEffect(() => {
    if (edit && sections_list.length > 0) {
      if (isNew) {
        const querySection = getQueryParam('section');
        const targetSection = (querySection && sections[querySection]) ? querySection : sections_list[0].id;
        if (!lazyArticle.section) {
          handleChange(targetSection, 'section');
        }
      } else {
        if (!lazyArticle.section || !sections[lazyArticle.section]) {
          handleChange(sections_list[0].id, 'section');
        }
      }
    }
  }, [edit, isNew, sections, sections_list, lazyArticle.section, handleChange]);

  useEffect(() => { if (!edit) setLazyArticle(article) }, [article, edit])

  useEffect(() => {
    if (!edit && uploadedImages.length > 0) {
      removeImagesNotInHtml(lazyArticle.html || '', uploadedImages)
      setUploadedImages([])
    }
  }, [edit, lazyArticle.html, uploadedImages])

  useEffect(() => {
    async function updateMentions() {
      try {
        let isDirty = false
        Object.keys(articles_index).forEach(id => {
          let { title } = articles_index[id]
          let mentions = document.querySelectorAll(`[data-id="${id}"]`)

          mentions.forEach((mention: any) => {
            if (mention.childNodes[1]
              && mention.childNodes[1].childNodes[1]
              && (mention.childNodes[1].childNodes[1].data !== title || mention.dataset.value !== title)
            ) {
              isDirty = true
              mention.childNodes[1].childNodes[1].data = title
              mention.dataset.value = title
            }
          })
        })

        if (isDirty) handleChange(articleElRef.current!.innerHTML, 'html', true)

      } catch (error) {
        console.error(error);
      }

    }
    if (articleElRef.current) updateMentions()
  }, [lazyArticle.html, articles_index, handleChange])

  if (loading) return <Skeleton.Article />

  return (
    <div className={`AC-Article__Page ${edit ? ' Edit--Active' : ''}`} style={{ paddingTop: edit ? pageTop : 0 }}>
      {edit &&
        <SaveBar
          navItems={navigation_items}
          section={lazyArticle.section}
          onChange={(v) => handleChange(v, 'section')}
          processing={editorProcessing}
          onCancel={handleCancel}
          onSave={saveAndFinishEdit}
          onDelete={handleDelete}
          isNew={isNew}
          isFirstArticle={isFirstArticle || isSectionChanged}
          isLastArticle={isLastArticle || isSectionChanged}
          onMoveUp={() => moveArticle('up')}
          onMoveDown={() => moveArticle('down')}
        />
      }


      <div id="AC_Article">
        <Page>
          <div className="AC-Article__Title">
            <InlineGrid columns={"1fr auto"}>
              {width <= 770 && <Button variant="plain" icon={MenuIcon} onClick={onActivateMobileNavigation} />}
              <TitleEditor value={lazyArticle.title} onChange={(v) => handleChange(v, 'title')} editAcive={edit} />
              {!edit && (
                <ButtonGroup>
                  <Button icon={lazyArticle.pinned ? PinFilledIcon : PinIcon} variant="tertiary" onClick={handlePin} />
                  <Button variant="tertiary" icon={EditIcon} onClick={toggleEdit} />
                </ButtonGroup>
              )}
            </InlineGrid>
          </div>
          <LastUpdated updated={lazyArticle.updated} />
          {edit
            ? (
              <ContentEditor
                value={lazyArticle.html || ''}
                onChange={(v) => handleChange(v, 'html')}
                onSave={saveAndFinishEdit}
                editActive={edit}
                onSetLoading={setEditorProcessing}
                onImageUploaded={handleImageUploaded}
                imageRef={`shops/${shop}/articles/${articleId}`}
                keepToolbarInView={pageTop}
              />
            )
            : <div className="AC-Article__Page-Content">
              <div className="ql-snow">
                <article id="article" ref={articleElRef} className="ql-editor" dangerouslySetInnerHTML={{ __html: lazyArticle.html || '' }}></article>
              </div>
            </div>
          }
        </Page>
      </div>

    </div>
  );
}


const LastUpdated = ({ updated }: { updated?: string }) => {
  if (!updated) return null

  return (
    <Box paddingBlockStart="300" paddingBlockEnd="200">
      <Text as="span" tone="subdued">
        Last updated: {DateTime.fromISO(updated).toLocaleString(DateTime.DATETIME_MED)}
      </Text>
    </Box>
  )
}

interface SaveBarProps {
  navItems: Array<{ label: string; id: string }>;
  section?: string;
  onChange: (val: string) => void;
  processing: boolean;
  onCancel: () => void;
  onSave: () => void;
  onDelete: () => void;
  isNew?: boolean;
  isFirstArticle: boolean;
  isLastArticle: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

const SaveBar = ({
  navItems,
  section,
  onChange,
  processing,
  onCancel,
  onSave,
  onDelete,
  isNew,
  isFirstArticle,
  isLastArticle,
  onMoveUp,
  onMoveDown
}: SaveBarProps) => {
  const handleChange = useCallback((val: string) => {
    onChange(val)
  }, [onChange])


  return (
    <div id="AC_Actions_Bar" className="AC-Actions-Bar">
      <Box paddingBlock="300" paddingInline="400">
        <InlineGrid columns={!isNew ? "auto 1fr auto" : "1fr auto"} alignItems="center" gap="400">
          <InlineStack gap="200" blockAlign="center" align="start">
            <Label id="section">Section</Label>
            <Box minWidth="15rem">
              <Select
                label=""
                labelHidden
                value={section}
                options={navItems.map(({ label, id }) => ({ label, value: id }))}
                onChange={handleChange}
              />
            </Box>

          </InlineStack>
          {!isNew && (
            <InlineStack gap="100" blockAlign="center" align="start">
              <Label id="order">Order</Label>
              <ButtonGroup variant="segmented">
                <Button
                  icon={ChevronUpIcon}
                  disabled={isFirstArticle}
                  onClick={onMoveUp}
                  accessibilityLabel="Move article up"
                />
                <Button
                  icon={ChevronDownIcon}
                  disabled={isLastArticle}
                  onClick={onMoveDown}
                  accessibilityLabel="Move article down"
                />
              </ButtonGroup>
            </InlineStack>
          )}
          {/* {processing &&
            <div style={{ justifyContent: 'flex-end', display: 'flex', alignItems: 'center' }}>
              <Spinner size="small" /> <span style={{ marginLeft: '0.8rem' }}></span>
            </div>
          } */}
          <ButtonGroup>
            {!isNew && (
              <Box paddingBlockStart="150" paddingInlineEnd="100">
                <DeleteButton
                  disabled={processing}
                  onDelete={onDelete}
                  tone="critical"
                  variant="tertiary"
                  icon={DeleteIcon} />

              </Box>
            )}
            <Button disabled={processing} onClick={onCancel}>Discard {!isNew ? "changes" : ""}</Button>
            <Button disabled={processing} variant="primary" onClick={onSave} >Save</Button>
          </ButtonGroup>
        </InlineGrid>

      </Box>
    </div >
  )
}



export default Article
